import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload as UploadIcon, FileCode, Plus, Trash2, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, FileUp } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { KINDS, UPLOAD_KINDS, kindOf } from "@/lib/kinds";
import { CATEGORIES } from "@/lib/categories";

const ALLOWED = [".md", ".py", ".txt", ".json", ".yaml", ".yml"];
const isAllowed = (name) => ALLOWED.some((e) => name.toLowerCase().endsWith(e));

// ponytail: line-based frontmatter grab (name:/description:), a YAML lib if skills ever nest
const fmField = (md, key) => {
  const m = (md || "").match(/^---\s*\n([\s\S]*?)\n---/);
  const line = m && m[1].split("\n").find((l) => l.trimStart().startsWith(`${key}:`));
  return line ? line.slice(line.indexOf(":") + 1).trim().replace(/^["']|["']$/g, "") : "";
};

export default function Upload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Destination: an org's private catalog (?org=<slug> pre-selects it) or the public gallery.
  // Always shown as an explicit picker — a silent default to the gallery is how uploads end up
  // in the wrong review queue.
  const [orgSlug, setOrgSlug] = useState(searchParams.get("org") || "");
  const [myOrgs, setMyOrgs] = useState([]);
  useEffect(() => {
    api.get("/orgs/mine")
      .then(({ data }) => setMyOrgs((data || []).filter((o) => o.membership_status === "active")))
      .catch(() => {});
  }, []);
  const [kind, setKind] = useState("skill");
  const cfg = kindOf(kind);
  const single = cfg.single; // single-file kinds (agent/command/output_style/claude_md): no helpers
  const [name, setName] = useState("");
  const [category, setCategory] = useState("General");
  const [shortDesc, setShortDesc] = useState("");
  const [priceCredits, setPriceCredits] = useState("1"); // creator-set price, in credits
  const [skillMd, setSkillMd] = useState("");
  const [files, setFiles] = useState([]); // [{filename, content}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {review_status, reasons, message} | {errors}
  const [manual, setManual] = useState(false); // user opted out of the folder-first flow
  const skillMdInput = useRef(null);
  const helperInput = useRef(null);
  const folderInput = useRef(null);
  // Upload-first for EVERY kind (2026-07-14): skills open on a "pick your folder" hero, the
  // single-file kinds on a "pick your .md" hero. The form is only the review/edit step.
  const chooser = !manual && !skillMd.trim();

  // Pull name + description out of SKILL.md frontmatter so the user never retypes them.
  const applyMeta = (md) => {
    const n = fmField(md, "name");
    const d = fmField(md, "description");
    if (n) setName((cur) => cur || n);
    if (d) setShortDesc((cur) => cur || d.slice(0, 200));
    return n;
  };

  const addEmptyFile = () => setFiles((f) => [...f, { filename: "", content: "" }]);
  const updateFile = (i, field, val) => setFiles((f) => f.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)));
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const readText = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = reject;
      r.readAsText(file);
    });

  const onPickSkillMd = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const md = await readText(file);
    setSkillMd(md);
    const n = applyMeta(md);
    // No frontmatter name (CLAUDE.md, bare commands)? The filename is the next-best prefill.
    if (!n) setName((cur) => cur || file.name.replace(/\.(md|txt)$/i, ""));
    setManual(true); // land on the form — it's the review/edit step
    toast.success(`Loaded ${file.name} — review and submit`);
  };

  // A folder pick's path is "<picked-folder>/sub/dir/file.ext" (webkitRelativePath); drop the
  // picked folder's own name so stored paths are relative to the skill root, matching what a
  // plain multi-file pick already produces (no leading segment).
  const relPath = (file) => {
    const p = file.webkitRelativePath || file.name;
    const parts = p.split("/");
    return parts.length > 1 ? parts.slice(1).join("/") : p;
  };

  const onPickHelpers = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    let md = "";
    for (const file of picked) {
      const path = relPath(file);
      if (path.toLowerCase() === "skill.md") {
        md = await readText(file);
        setSkillMd(md);
        continue;
      }
      if (!isAllowed(path)) {
        toast.error(`Skipped ${path} — type not allowed`);
        continue;
      }
      const content = await readText(file);
      setFiles((f) => [...f, { filename: path, content }]);
    }
    setManual(true); // land on the form either way — it's the review/edit step
    if (md) {
      const n = applyMeta(md);
      if (n) toast.success(`Loaded ${n} — review and submit`);
      else toast.warning("Loaded your folder — add a name, we couldn't find one in SKILL.md");
    } else if (picked.length) {
      toast.warning("No SKILL.md at the folder root — paste it in below");
    }
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!skillMd.trim()) {
      toast.error(`${cfg.mainLabel} is required`);
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        kind,
        name: name.trim() || undefined,
        category: category || "General",
        short_description: shortDesc.trim(),
        // Price in credits → cents (1 credit = 100 cents). Clamp to a sane non-negative integer;
        // the server re-validates against MAX_SKILL_PRICE_CENTS. Org members don't price (2026-07-14).
        price_cents: myOrgs.length ? 0 : Math.max(0, Math.round((parseFloat(priceCredits) || 0) * 100)),
        skill_md: skillMd,
        // Only skills carry helper files; single-file kinds are one .md.
        files: single ? [] : files.filter((f) => f.filename.trim()).map((f) => ({ filename: f.filename.trim(), content: f.content })),
        org_slug: orgSlug || undefined,
      };
      const { data } = await api.post("/skills/upload", payload);
      setResult(data);
      if (data.review_status === "rejected") {
        toast.error("Flagged by the safety scan — you can appeal");
      } else if (data.version?.status === "published") {
        toast.success(`Published to ${orgSlug}'s catalog`);
      } else if (data.review_status === "approved") {
        toast.success("Approved — ready to publish");
      } else {
        toast.success("Sent to your org admin for review");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setResult({ errors: detail.errors });
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          <UploadIcon className="h-5 w-5 text-[color:var(--rb-accent)]" />
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Upload {cfg.label}</h1>
          <span className="ml-2 text-xs text-[color:var(--rb-text-faint)]">to</span>
          <Select value={orgSlug || "__public__"} onValueChange={(v) => setOrgSlug(v === "__public__" ? "" : v)}>
            <SelectTrigger data-testid="upload-destination" className="h-8 w-auto gap-1.5 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
              {/* Org members can't publish publicly (2026-07-13), so a gallery upload is a personal draft for them. */}
              <SelectItem value="__public__">{myOrgs.length ? "Just for me (private)" : "Public gallery"}</SelectItem>
              {myOrgs.map((o) => (
                <SelectItem key={o.slug} value={o.slug}>{o.name} (org)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-[color:var(--rb-text-faint)]">
          {single ? (
            <>Bring your own <span className="rb-mono text-[color:var(--rb-accent)]">{cfg.mainLabel}</span>
            {cfg.lands ? <> — downloads as a single file for <span className="rb-mono text-[color:var(--rb-accent)]">{cfg.lands}</span></> : null}.</>
          ) : (
            <>Point us at your skill folder — we take it from there.</>
          )}
          {" "}Every upload is scanned for malware and prompt-injection — clean uploads are approved on the spot.
        </p>
        <div className="mt-4 inline-flex flex-wrap gap-1 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-1" data-testid="upload-kind-toggle">
          {UPLOAD_KINDS.map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`upload-kind-${k}`}
              className={`rounded-lg px-4 py-1.5 text-xs font-medium transition-colors ${
                kind === k ? "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]" : "text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]"
              }`}>
              {KINDS[k].label}
            </button>
          ))}
        </div>
      </div>

      {/* Hidden pickers — used by both the heroes and the form */}
      <input ref={skillMdInput} type="file" accept=".md,.txt" className="hidden" onChange={onPickSkillMd} />
      <input ref={helperInput} type="file" multiple accept=".md,.py,.txt,.json,.yaml,.yml" className="hidden" onChange={onPickHelpers} />
      <input ref={folderInput} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={onPickHelpers} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Folder-first hero (skills), or the review/edit form */}
        <div className="space-y-5">
          {chooser ? (
          single ? (
          <div className="rounded-2xl border border-dashed border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-10 text-center" data-testid="upload-file-hero">
            <FileUp className="mx-auto h-8 w-8 text-[color:var(--rb-accent)]" />
            <h2 className="mt-3 text-lg font-medium text-[color:var(--rb-text)]">Upload your {cfg.mainLabel}</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-[color:var(--rb-text-faint)]">
              One .md file — we pull the name and description out of it. You just review and submit.
            </p>
            <Button onClick={() => skillMdInput.current?.click()} data-testid="upload-pick-file-hero"
              className="mt-5 h-11 rounded-xl bg-[color:var(--rb-accent)] px-6 text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              Choose file
            </Button>
            <div className="mt-4 text-xs text-[color:var(--rb-text-faint)]">
              or <button onClick={() => setManual(true)} className="text-[color:var(--rb-accent)] hover:underline" data-testid="upload-manual">fill it in manually</button>
            </div>
          </div>
          ) : (
          <div className="rounded-2xl border border-dashed border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-10 text-center" data-testid="upload-folder-hero">
            <FileUp className="mx-auto h-8 w-8 text-[color:var(--rb-accent)]" />
            <h2 className="mt-3 text-lg font-medium text-[color:var(--rb-text)]">Upload your skill folder</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-[color:var(--rb-text-faint)]">
              We read SKILL.md, pull the name and description out of it, and bring every helper file along. You just review and submit.
            </p>
            <Button onClick={() => folderInput.current?.click()} data-testid="upload-pick-folder-hero"
              className="mt-5 h-11 rounded-xl bg-[color:var(--rb-accent)] px-6 text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              Choose folder
            </Button>
            <div className="mt-4 text-xs text-[color:var(--rb-text-faint)]">
              <button onClick={() => helperInput.current?.click()} className="text-[color:var(--rb-accent)] hover:underline" data-testid="upload-pick-files-hero">pick individual files</button>
              {" · "}
              <button onClick={() => setManual(true)} className="text-[color:var(--rb-accent)] hover:underline" data-testid="upload-manual">fill it in manually</button>
            </div>
          </div>
          )
          ) : (
          <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-[color:var(--rb-text-faint)]">Name <span className="text-[color:var(--rb-gold)]">*required</span></label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="invoice-parser" data-testid="upload-name-input"
                autoFocus={!name.trim()}
                className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            </div>
            <div>
              <label className="text-xs text-[color:var(--rb-text-faint)]">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="upload-category-input"
                  className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className={`grid grid-cols-1 gap-3 ${myOrgs.length ? "" : "sm:grid-cols-[1fr_160px]"}`}>
            <div>
              <label className="text-xs text-[color:var(--rb-text-faint)]">Short description</label>
              <Input value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} placeholder="What does this skill do?" data-testid="upload-shortdesc-input"
                className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            </div>
            {/* Org members neither sell publicly nor price anything (2026-07-14). */}
            {!myOrgs.length && (
            <div>
              <label className="text-xs text-[color:var(--rb-text-faint)]">Price (credits)</label>
              <Input type="number" min="0" step="1" value={priceCredits} onChange={(e) => setPriceCredits(e.target.value)} placeholder="1" data-testid="upload-price-input"
                className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            </div>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs text-[color:var(--rb-text-faint)]"><FileCode className="h-3.5 w-3.5" /> {cfg.mainLabel} <span className="text-[color:var(--rb-gold)]">*required</span></label>
              <Button variant="ghost" size="sm" onClick={() => skillMdInput.current?.click()} className="h-7 text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]" data-testid="upload-load-skillmd">
                <FileUp className="mr-1 h-3.5 w-3.5" /> Load .md
              </Button>
            </div>
            <Textarea value={skillMd} onChange={(e) => setSkillMd(e.target.value)} data-testid="upload-skillmd-textarea"
              placeholder={cfg.placeholder}
              className="min-h-[220px] resize-y rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]" />
          </div>

          {/* Helper files — skills only; single-file kinds are one .md */}
          {!single && (
          <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[color:var(--rb-text)]">Helper files <span className="text-xs text-[color:var(--rb-text-faint)]">(optional)</span></span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => helperInput.current?.click()} className="border-[color:var(--rb-border)] text-xs" data-testid="upload-pick-files">
                  <FileUp className="mr-1 h-3.5 w-3.5" /> Upload files
                </Button>
                <Button variant="outline" size="sm" onClick={() => folderInput.current?.click()} className="border-[color:var(--rb-border)] text-xs" data-testid="upload-pick-folder">
                  <FileUp className="mr-1 h-3.5 w-3.5" /> Upload folder
                </Button>
                <Button variant="outline" size="sm" onClick={addEmptyFile} className="border-[color:var(--rb-border)] text-xs" data-testid="upload-add-file">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add file
                </Button>
              </div>
            </div>
            {files.length === 0 ? (
              <p className="mt-3 text-xs text-[color:var(--rb-text-faint)]">No helper files. Allowed types: {ALLOWED.join(", ")}.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {files.map((f, i) => (
                  <div key={i} className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-3" data-testid="upload-file-row">
                    <div className="flex items-center gap-2">
                      <Input value={f.filename} onChange={(e) => updateFile(i, "filename", e.target.value)} placeholder="scripts/parse.py"
                        className="h-8 rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] text-xs text-[color:var(--rb-text)]" />
                      <Button variant="ghost" size="sm" onClick={() => removeFile(i)} className="h-8 w-8 p-0 text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-danger,#f87171)]" data-testid="upload-remove-file">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Textarea value={f.content} onChange={(e) => updateFile(i, "content", e.target.value)} placeholder="File contents…"
                      className="mt-2 min-h-[90px] resize-y rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] text-xs text-[color:var(--rb-text)]" />
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={submit} disabled={submitting} data-testid="upload-submit-button"
              className="h-11 rounded-xl bg-[color:var(--rb-accent)] px-6 text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)] disabled:opacity-50">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scanning…</> : <><ShieldCheck className="mr-2 h-4 w-4" /> Scan & submit</>}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/my-skills")} className="text-[color:var(--rb-text-faint)]">Cancel</Button>
          </div>
          </>
          )}

          {/* Result */}
          {result && (
            <div data-testid="upload-result" className={`rounded-2xl border p-4 ${result.errors ? "border-[color:rgba(248,113,113,0.4)]" : result.review_status === "rejected" ? "border-[color:rgba(248,113,113,0.4)]" : "border-[color:rgba(45,212,191,0.4)]"} bg-[color:var(--rb-obsidian)]`}>
              {result.errors ? (
                <>
                  <div className="flex items-center gap-2 text-[color:#f87171]"><AlertTriangle className="h-4 w-4" /> <span className="font-medium">Upload rejected</span></div>
                  <ul className="mt-2 list-inside list-disc text-sm text-[color:var(--rb-text-dim)]">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </>
              ) : result.review_status === "rejected" ? (
                <>
                  <div className="flex items-center gap-2 text-[color:#f87171]"><AlertTriangle className="h-4 w-4" /> <span className="font-medium">Flagged by safety scan — rejected</span></div>
                  <p className="mt-1 text-sm text-[color:var(--rb-text-dim)]">{result.message}</p>
                  {result.reasons?.length ? (
                    <ul className="mt-2 list-inside list-disc text-xs text-[color:var(--rb-text-faint)]">
                      {result.reasons.slice(0, 8).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  ) : null}
                  <Button size="sm" variant="outline" onClick={() => navigate("/my-skills")} data-testid="upload-appeal-link"
                    className="mt-3 rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)]">
                    Appeal from My Artifacts
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-[color:var(--rb-accent)]"><CheckCircle2 className="h-4 w-4" /> <span className="font-medium">
                    {result.version?.status === "published" ? "Published to your org's catalog"
                      : result.review_status === "approved" ? "Approved — ready to publish"
                      : "Sent for review"}
                  </span></div>
                  <p className="mt-1 text-sm text-[color:var(--rb-text-dim)]">{result.message}</p>
                  <Button size="sm" onClick={() => navigate(orgSlug ? `/orgs/${orgSlug}/artifacts/${result.id}` : "/my-skills")} className="mt-3 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                    {orgSlug ? "View in org catalog" : "View in My Artifacts"}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Side rail */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-[color:var(--rb-text)]"><ShieldCheck className="h-4 w-4 text-[color:var(--rb-accent)]" /> Reviewed for safety</div>
            {/* Outcome only, never mechanics (2026-07-14): naming the reviewer or the
                auto-approve path hands attackers a target. */}
            <p className="mt-3 text-xs text-[color:var(--rb-text-dim)]">Every upload is reviewed for safety before anyone can install it. Uploads that don't pass are held — your org admin decides for org uploads; gallery rejections can be appealed to the Runebox review team.</p>
            <p className="mt-3 text-xs text-[color:var(--rb-text-faint)]">Allowed types: {ALLOWED.join(", ")}. Binaries/executables are blocked.</p>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
